import type { V2Component } from './autoSelectTypes'
import { componentIsFromPreferredSupplier, componentIsFromSupplier } from './supplierPreferenceUtils'

export type CatalogFetchScope = 'preferred_suppliers' | 'full_catalog'

export type DCCableSelectionTrace = {
  totalRunLength: number
  plannedReelLength: number
  plannedQuantity: number
  plannedPurchasedLength: number
  lengthsToTry: number[]
  preferredStockedLengths: number[]
  selectedReelLength?: number
  selectedSupplier?: string
  catalogFetchScope?: CatalogFetchScope
  reelLengthEscalated: boolean
  supplierEscalated: boolean
  catalogScopeEscalated: boolean
  catalogUnstockedFallback?: boolean
}

export type ACIsolatorStockByPhase = Record<string, number[]>
export type ACIsolatorStockBySupplier = Record<string, ACIsolatorStockByPhase>

export type IsolatorSelectionTrace = {
  catalogFetchScope?: CatalogFetchScope
  catalogScopeEscalated: boolean
  supplierEscalated: boolean
  outputCurrent?: number
  minimumRating?: number
  inverterPhase?: string
  phaseAttempts?: string[]
  preferredStockBySupplier?: ACIsolatorStockBySupplier
  orderedSuppliers?: string[]
  preferredStockedRatings?: number[]
  selectedPhase?: string
  selectedRating?: number
  selectedSupplier?: string
  phaseEscalated: boolean
  sizeEscalated: boolean
  catalogUnstockedFallback?: boolean
}

export const ISOLATOR_PHASES = ['single_phase', 'three_phase'] as const

function sortUniqueNumbers(values: number[]): number[] {
  return [...new Set(values.filter(Number.isFinite))].sort((a, b) => a - b)
}

function collectAdequateRatings(
  components: V2Component[],
  minimumRating: number,
  filter: { phase?: string; supplier?: string; preferredSuppliers?: string[] }
): number[] {
  const ratings: number[] = []
  for (const component of components) {
    if (filter.phase && component.phase_type !== filter.phase) continue
    if (filter.supplier && !componentIsFromSupplier(component, filter.supplier)) continue
    if (filter.preferredSuppliers?.length && !componentIsFromPreferredSupplier(component, filter.preferredSuppliers)) {
      continue
    }
    const rating = Number(component.current_rating)
    if (Number.isFinite(rating) && rating >= minimumRating) ratings.push(rating)
  }
  return sortUniqueNumbers(ratings)
}

export function summarizePreferredDCCableReelLengths(
  components: V2Component[],
  orderedSuppliers: string[]
): number[] {
  if (!orderedSuppliers.length) {
    return sortUniqueNumbers(components.map((component) => Number(component.cable_length)))
  }

  const lengths: number[] = []
  for (const component of components) {
    if (!component.ordering_v2?.some((ordering) => orderedSuppliers.includes(ordering.distributor))) continue
    const length = Number(component.cable_length)
    if (Number.isFinite(length)) lengths.push(length)
  }
  return sortUniqueNumbers(lengths)
}

/** Adequate-rating stock at each preferred supplier, split by 1PH and 3PH. */
export function summarizeACIsolatorPreferredStock(
  components: V2Component[],
  orderedSuppliers: string[],
  minimumRating: number
): ACIsolatorStockBySupplier {
  const stock: ACIsolatorStockBySupplier = {}
  for (const supplier of orderedSuppliers) {
    const byPhase: ACIsolatorStockByPhase = {}
    for (const phase of ISOLATOR_PHASES) {
      const ratings = collectAdequateRatings(components, minimumRating, { phase, supplier })
      if (ratings.length) byPhase[phase] = ratings
    }
    if (Object.keys(byPhase).length) stock[supplier] = byPhase
  }
  return stock
}

export function summarizeDCIsolatorStockedRatings(
  components: V2Component[],
  orderedSuppliers: string[]
): number[] {
  const ratings: number[] = []
  for (const component of components) {
    if (orderedSuppliers.length && !componentIsFromPreferredSupplier(component, orderedSuppliers)) continue
    const rating = Number(component.current_rating)
    if (Number.isFinite(rating)) ratings.push(rating)
  }
  return sortUniqueNumbers(ratings)
}

export function componentSupplierKey(component: V2Component, orderedSuppliers: string[]): string | undefined {
  for (const supplier of orderedSuppliers) {
    if (componentIsFromSupplier(component, supplier)) return supplier
  }
  return component.ordering_v2?.[0]?.distributor
}
